/**
 * コーディングテストの練習 (標準入力を読んで標準出力に書く形) の原稿の読み方。
 *
 * 原稿は content/coding-test/<slug>.md (日本語)。読み込みは coding-test.server.ts が持つ。
 * 隠れたテストは content/coding-test/<slug>/tests/NN.in と NN.out。NN は 01 からの 2 桁で、.out は模範解答から
 * tools/coding-test/verify.ts --write-expected が作る (手で書かない)。
 * 大きい入力は置かず、coding-test-gen/<slug>.ts の生成器が固定の乱数で作る。その期待する出力は、出力そのものではなく指紋の NN.sha256 を置く (coding-test-gen.ts)。
 *
 * 関数を書く形のコーディング面接問題 (coding-interview.ts) とは別の物。向こうは関数の名前と引数が決まっていて、
 * こちらは入力の読み方、出力の書き方、制約の大きさまでが問題の一部になる。
 *
 * 前付きの欄
 * - kind  problem は 1 問ずつの練習 (spec は仕様を読んで作る実装、fix は直す練習で、あとの PR で足す)
 * - shortTitle  左の並び (ProblemSidebar) に出す短い題。全角 12 字以内 (半角は 0.5 字)。無ければ title を出す
 * - tier  A (for と if と配列)、B (累積和、二分探索、全探索)、C (グラフ、DP)。頁には「やさしい」「ふつう」「やや難しい」と出す (A、B、C はデータの欄名で、受講生には見せない)
 * - estimatedMinutes  解く時間の目安。公開されているスキルチェックの解答時間の中央値に合わせる
 * - timeLimitMs  1 テストの上限。模範解答が Pyodide で 1/3 以内で通ることを台本が確かめる
 * - compare  exact (右の空白と末尾の空行を落として完全一致) か float (数の並びを誤差 1e-6 で比べる)
 * - groups  部分点の群。[小さい入力=1-8, 大きい入力=9-11] の形で、テストの番号の範囲を 1 から切れ目なく並べる
 *
 * 地の文に半角のコロンは書かない。木、連結リスト、トライ木は入れない (中途の標準ではないため)
 */
import {
  type MdSection,
  SLUG_PATTERN,
  codeFences,
  parseDate,
  parseFrontMatter,
  parseList,
  parseSections,
  requireFields,
  slugFromPath,
} from "@/lib/md-entry";
import { SHORT_TITLE_MAX_WIDTH, titleWidth } from "@/lib/problem-short-titles";
import type { CompareMode, JudgeLanguage } from "@/lib/stdio-judge/types";

export const CODING_TEST_INDEX_PATH = "/interview-questions/coding/coding-test";

export function codingTestPath(slug: string) {
  return `${CODING_TEST_INDEX_PATH}/${slug}`;
}

/** 静的な頁 (模擬試験 mock、標準入力の読み方 stdin) の住所と取り合うので、問題の slug に使えない */
export const RESERVED_SLUGS = ["mock", "stdin"] as const;

export const CODING_TEST_KINDS = [
  { id: "problem", label: "標準入出力の問題" },
  { id: "spec", label: "仕様を読んで作る" },
  { id: "fix", label: "直す" },
] as const;
export type CodingTestKind = (typeof CODING_TEST_KINDS)[number]["id"];

export const CODING_TEST_TIERS = [
  { id: "A", label: "やさしい", note: "for と if と配列で解ける" },
  { id: "B", label: "ふつう", note: "累積和、二分探索、全探索など、型を 1 つ選んで解く" },
  { id: "C", label: "やや難しい", note: "グラフや動的計画法まで要る" },
] as const;
export type CodingTestTier = (typeof CODING_TEST_TIERS)[number]["id"];

/* 型の一覧は、問題を足す PR が同じ行をいじってぶつからないよう、先に全部置いておく */
export const CODING_TEST_TOPICS = [
  { id: "io-basics", label: "入出力の基本" },
  { id: "branching", label: "条件分岐" },
  { id: "string", label: "文字列の処理" },
  { id: "array", label: "配列の処理" },
  { id: "brute-force", label: "全探索" },
  { id: "math", label: "数と計算" },
  { id: "sort", label: "並べ替えと貪欲" },
  { id: "prefix-sum", label: "累積和" },
  { id: "binary-search", label: "二分探索" },
  { id: "grid", label: "迷路と盤面" },
  { id: "dp", label: "動的計画法" },
] as const;
export type CodingTestTopicId = (typeof CODING_TEST_TOPICS)[number]["id"];

export const CODING_TEST_LANGUAGES: { id: JudgeLanguage; label: string }[] = [
  { id: "python", label: "Python" },
  { id: "javascript", label: "JavaScript" },
];

export const CODING_TEST_SECTION_IDS = ["problem", "examples", "starter", "hints", "solution", "complexity", "mistakes"] as const;
export type CodingTestSectionId = (typeof CODING_TEST_SECTION_IDS)[number];

export type CodingTestGroup = { name: string; from: number; to: number };

export type CodingTestMeta = {
  slug: string;
  title: string;
  shortTitle: string;
  order: number;
  kind: CodingTestKind;
  tier: CodingTestTier;
  topic: CodingTestTopicId;
  summary: string;
  estimatedMinutes: number;
  timeLimitMs: number;
  compare: CompareMode;
  groups: CodingTestGroup[];
  verifiedPython: string;
  verifiedNode: string;
  verifiedAt: string;
};

export type CodingTestProblem = CodingTestMeta & { sections: MdSection<CodingTestSectionId>[] };

const FIELDS = [
  "slug",
  "title",
  "order",
  "kind",
  "tier",
  "topic",
  "summary",
  "estimatedMinutes",
  "timeLimitMs",
  "compare",
  "groups",
  "verifiedPython",
  "verifiedNode",
  "verifiedAt",
] as const;

function parseGroups(value: string, source: string): CodingTestGroup[] {
  const groups = parseList(value, "groups", source).map((item) => {
    const match = /^(.+)=(\d+)-(\d+)$/.exec(item);
    if (!match) throw new Error(`${source} の groups は 名前=1-8 の形で書きます (${item})`);
    return { name: match[1].trim(), from: Number(match[2]), to: Number(match[3]) };
  });
  if (groups.length === 0) throw new Error(`${source} の groups が空です`);
  let next = 1;
  for (const group of groups) {
    if (group.from !== next || group.to < group.from) {
      throw new Error(`${source} の groups は 1 から切れ目なく並べます (${group.name})`);
    }
    next = group.to + 1;
  }
  return groups;
}

/** 例の節は ### 例 N ごとに、入力と出力の text の囲みを 1 つずつ持つ */
function checkExamples(section: MdSection, source: string) {
  if (section.items.length < 2) throw new Error(`${source} の例は 2 つ以上置きます`);
  for (const item of section.items) {
    const fences = codeFences(item.body);
    if (fences.length !== 2 || fences.some((fence) => fence.lang !== "text")) {
      throw new Error(`${source} の ${item.heading} は入力と出力の text の囲み 2 つで書きます`);
    }
  }
}

/** 書き始めと解答は、言語ごとの ### を決まった順で持ち、それぞれにその言語の囲みが 1 つある */
function checkPerLanguage(section: MdSection, prefix: string, source: string) {
  const ids = section.items.map((item) => item.id).join(",");
  const want = CODING_TEST_LANGUAGES.map((language) => `${prefix}-${language.id}`).join(",");
  if (ids !== want) throw new Error(`${source} の ${section.id} の並びが決まりと違います (${ids})`);
  for (const [index, language] of CODING_TEST_LANGUAGES.entries()) {
    const fences = codeFences(section.items[index].body);
    if (fences.length !== 1 || fences[0].lang !== language.id) {
      throw new Error(`${source} の ${section.id} の ${language.label} は ${language.id} の囲み 1 つで書きます`);
    }
  }
}

export function parseCodingTestProblem(raw: string, source: string): CodingTestProblem {
  const { fields, body } = parseFrontMatter(raw, source);
  const f = requireFields(fields, FIELDS, source);
  if (!SLUG_PATTERN.test(f.slug)) throw new Error(`${source} の slug が英小文字とハイフンではありません`);
  if ((RESERVED_SLUGS as readonly string[]).includes(f.slug)) throw new Error(`${source} の slug ${f.slug} は静的な頁の住所なので使えません`);
  const kind = CODING_TEST_KINDS.find((item) => item.id === f.kind);
  if (!kind) throw new Error(`${source} の kind ${f.kind} は決まりにありません`);
  const tier = CODING_TEST_TIERS.find((item) => item.id === f.tier);
  if (!tier) throw new Error(`${source} の tier ${f.tier} は決まりにありません`);
  const topic = CODING_TEST_TOPICS.find((item) => item.id === f.topic);
  if (!topic) throw new Error(`${source} の topic ${f.topic} は決まりにありません`);
  if (f.compare !== "exact" && f.compare !== "float") throw new Error(`${source} の compare は exact か float で書きます`);
  const order = Number(f.order);
  if (!Number.isInteger(order) || order < 1) throw new Error(`${source} の order は 1 からの整数で書きます`);
  const estimatedMinutes = Number(f.estimatedMinutes);
  if (!Number.isInteger(estimatedMinutes) || estimatedMinutes < 1) throw new Error(`${source} の estimatedMinutes は分の整数で書きます`);
  const timeLimitMs = Number(f.timeLimitMs);
  if (!Number.isInteger(timeLimitMs) || timeLimitMs < 500 || timeLimitMs > 10000) {
    throw new Error(`${source} の timeLimitMs は 500 以上 10000 以下のミリ秒で書きます`);
  }

  const sections = parseSections(body, source, CODING_TEST_SECTION_IDS);
  const problem = sections[0];
  const itemIds = problem.items.map((item) => item.id).join(",");
  if (itemIds !== "input-format,output-format,constraints") {
    throw new Error(`${source} の問題の節は 入力の形式、出力の形式、制約 の順で書きます (${itemIds})`);
  }
  checkExamples(sections[1], source);
  checkPerLanguage(sections[2], "starter", source);
  if (sections[3].items.length < 2) throw new Error(`${source} のヒントは 2 つ以上置きます`);
  checkPerLanguage(sections[4], "solution", source);

  const shortTitle = fields.get("shortTitle") ?? f.title;
  if (titleWidth(shortTitle) > SHORT_TITLE_MAX_WIDTH) {
    throw new Error(`${source} の shortTitle は全角 ${SHORT_TITLE_MAX_WIDTH} 字以内で書きます (${shortTitle} は ${titleWidth(shortTitle)} 字)`);
  }

  return {
    slug: f.slug,
    title: f.title,
    shortTitle,
    order,
    kind: kind.id,
    tier: tier.id,
    topic: topic.id,
    summary: f.summary,
    estimatedMinutes,
    timeLimitMs,
    compare: f.compare,
    groups: parseGroups(f.groups, source),
    verifiedPython: f.verifiedPython,
    verifiedNode: f.verifiedNode,
    verifiedAt: parseDate(f.verifiedAt, "verifiedAt", source),
    sections,
  };
}

/** 置き場のパスと中身の組から全部を読み、order の順に並べる。slug と order の重なりは投げる */
export function loadCodingTestProblems(files: Record<string, string>): CodingTestProblem[] {
  const list: CodingTestProblem[] = [];
  for (const [path, raw] of Object.entries(files)) {
    const problem = parseCodingTestProblem(raw, path);
    if (problem.slug !== slugFromPath(path)) throw new Error(`${path} の slug ${problem.slug} がファイル名と違います`);
    if (list.some((other) => other.slug === problem.slug)) throw new Error(`slug ${problem.slug} が 2 本あります`);
    if (list.some((other) => other.order === problem.order)) throw new Error(`order ${problem.order} が 2 本あります`);
    list.push(problem);
  }
  return list.sort((a, b) => a.order - b.order);
}

export function codingTestMeta(problem: CodingTestProblem): CodingTestMeta {
  const { sections: _sections, ...meta } = problem;
  return meta;
}

export function codingTestTierLabel(id: CodingTestTier) {
  return CODING_TEST_TIERS.find((tier) => tier.id === id)!.label;
}

export function codingTestTopicLabel(id: CodingTestTopicId) {
  return CODING_TEST_TOPICS.find((topic) => topic.id === id)!.label;
}

function section(problem: CodingTestProblem, id: CodingTestSectionId) {
  return problem.sections.find((s) => s.id === id)!;
}

/** 例の入力と出力と一言 (## 例 の ### ごと)。入力は 1 つ目の囲み、出力は 2 つ目の囲み */
export type CodingTestExample = { label: string; note: string; input: string; output: string };

export function codingTestExamples(problem: CodingTestProblem): CodingTestExample[] {
  return section(problem, "examples").items.map((item) => {
    const fences = codeFences(item.body);
    const note = item.body.split("```")[0].trim();
    return { label: item.heading, note, input: fences[0].code, output: fences[1].code };
  });
}

export function codingTestStarter(problem: CodingTestProblem, language: JudgeLanguage): string {
  const item = section(problem, "starter").items.find((i) => i.id === `starter-${language}`)!;
  return codeFences(item.body)[0].code.replace(/\n$/, "") + "\n";
}

export function codingTestSolution(problem: CodingTestProblem, language: JudgeLanguage): string {
  const item = section(problem, "solution").items.find((i) => i.id === `solution-${language}`)!;
  return codeFences(item.body)[0].code.replace(/\n$/, "") + "\n";
}

export function codingTestHints(problem: CodingTestProblem): string[] {
  return section(problem, "hints").items.map((item) => item.body);
}

/** テストの番号 (1 から) が入る群の名前 */
export function groupOfTest(meta: Pick<CodingTestMeta, "groups">, number: number): string | undefined {
  return meta.groups.find((group) => number >= group.from && number <= group.to)?.name;
}

export type TestFileKind = "in" | "out" | "sha256";

/** 隠れたテストの置き場のファイル名 (01.in、01.out、01.sha256) の読み方 */
export function parseTestFileName(name: string): { number: number; kind: TestFileKind } | null {
  const match = /^(\d{2})\.(in|out|sha256)$/.exec(name);
  return match ? { number: Number(match[1]), kind: match[2] as TestFileKind } : null;
}

export function testFileName(number: number, kind: TestFileKind) {
  return `${String(number).padStart(2, "0")}.${kind}`;
}
